import { DEFAULT_REGION_KEY, CONFIGURABLE_REGIONS } from './constants/region'
import { getRegionFromName } from '@constants/region'
import { isNull, isUndefined } from '@utils/checkers'
import { getRegion, getRegionBaseUrl } from '@storyblok/region-helper'
import { validateApiEndpoint } from './utils/dynamicBackendApiEndpoint'

export type AvailableRegionConfig =
  | 'eu'
  | 'us-east-1'
  | 'ca-central-1'
  | 'ap-southeast-2'
  | 'cn'
  | 'local'

/**
 * production: anything used by our customers
 * preview: any deployment not used by our customers
 * development: local development
 */
export type Environment = 'production' | 'development' | 'preview'

const isEnvironment = (env: unknown): env is Environment =>
  env === 'production' || env === 'development' || env === 'preview'

// Just used for testing. See `environment` for usage in app.
export const parseEnvironment = (
  environmentVariable: string | undefined,
  isDevMode: boolean,
): Environment =>
  isEnvironment(environmentVariable)
    ? // If the environment variable is set, always use it
      environmentVariable
    : isDevMode
      ? // If it's not set, and we're running in local development mode, use development, else use production
        'development'
      : 'production'

export const environment = (): Environment =>
  parseEnvironment(import.meta.env.VITE_ENVIRONMENT, import.meta.env.DEV)

/* Start dynamic backend api endpoint */
if (process.env.VUE_APP_STORYBLOK_API_ENDPOINT) {
  window.STORYBLOK_API_ENDPOINT = process.env.VUE_APP_STORYBLOK_API_ENDPOINT
}

if (process.env.VUE_APP_STORYBLOK_PLUGIN_ENDPOINT) {
  window.STORYBLOK_PLUGIN_ENDPOINT =
    process.env.VUE_APP_STORYBLOK_PLUGIN_ENDPOINT
}

if (environment() !== 'production') {
  const apiEndpoint = window.sessionStorage.getItem('apiendpoint')
  if (apiEndpoint && validateApiEndpoint(apiEndpoint)) {
    // eslint-disable-next-line no-console -- not visible in production
    console.info('Loaded apiendpoint from session storage:', apiEndpoint)
    window.STORYBLOK_API_ENDPOINT = apiEndpoint
  }

  const pluginEndpoint = window.sessionStorage.getItem('pluginendpoint')
  if (pluginEndpoint && validateApiEndpoint(pluginEndpoint)) {
    // eslint-disable-next-line no-console -- not visible in production
    console.info('Loaded pluginendpoint from session storage:', pluginEndpoint)
    window.STORYBLOK_PLUGIN_ENDPOINT = pluginEndpoint
  }
}
/* End dynamic backend api endpoint */

if (process.env.VUE_APP_STORYBLOK_REGION) {
  const configuredRegion = process.env.VUE_APP_STORYBLOK_REGION.toLowerCase()
  if (CONFIGURABLE_REGIONS.includes(configuredRegion)) {
    window.STORYBLOK_REGION = configuredRegion as AvailableRegionConfig
  }
}

if (process.env.VUE_APP_IGNORING_REGIONS) {
  window.IGNORING_REGIONS =
    process.env.VUE_APP_IGNORING_REGIONS.toLowerCase() === 'true'
}

export type RegionSetting = {
  s3Endpoint: string
  bucket: string
  imageService: string
  imgPrefix?: string
  apiEndpoint?: string
}

const regionSetting: Record<AvailableRegionConfig, RegionSetting> = {
  cn: {
    s3Endpoint: 's3.cn-north-1.amazonaws.com.cn',
    bucket: 'storyblokcn',
    imageService: 'img3.storyblok.com',
    imgPrefix: 'http://storyblokcn.s3.cn-north-1.amazonaws.com.cn/',
  },
  eu: {
    s3Endpoint: 's3.amazonaws.com',
    bucket: 'a.storyblok.com',
    imageService: 'img2.storyblok.com',
  },
  'us-east-1': {
    s3Endpoint: 's3.amazonaws.com',
    bucket: 'a-us.storyblok.com',
    imageService: 'a-us.storyblok.com',
    apiEndpoint: 'https://api-us.storyblok.com',
  },
  'ca-central-1': {
    s3Endpoint: 's3.ca-central-1.amazonaws.com',
    bucket: 'a-ca.storyblok.com',
    imageService: 'a-ca.storyblok.com',
    apiEndpoint: 'https://api-ca.storyblok.com',
  },
  'ap-southeast-2': {
    s3Endpoint: 's3.ap-southeast-2.amazonaws.com',
    bucket: 'a-ap.storyblok.com',
    imageService: 'a-ap.storyblok.com',
    apiEndpoint: 'https://api-ap.storyblok.com',
  },
  local: {
    s3Endpoint: 's3.amazonaws.com',
    bucket: 'a.storyblok.com',
    imageService: 'img2.storyblok.com',
  },
}

const isRegionDefined = typeof window.STORYBLOK_REGION !== 'undefined'
let region: AvailableRegionConfig =
  typeof window.STORYBLOK_REGION !== 'undefined'
    ? window.STORYBLOK_REGION
    : DEFAULT_REGION_KEY

if (environment() === 'development' && !isRegionDefined) {
  region = 'local'
}

/**
 * Given a space ID, get the base URL to call for the space endpoints of that space.
 * - In production, the ID is mapped to a region-specific URL.
 * - In other environments, there are no regions and the URL is instead read from the environment variables.
 * @param spaceId
 */
export const getBaseUrlForSpaceId = (spaceId: number): string | undefined => {
  const apiEndpointFromEnv = window.STORYBLOK_API_ENDPOINT
  if (apiEndpointFromEnv) {
    // If we are not in the main production cluster, there are no regions to consider
    return apiEndpointFromEnv
  }
  // We're on the main production cluster
  const region = getRegion(spaceId)
  if (!region) {
    return undefined
  }
  return getRegionBaseUrl(region)
}

export default {
  get<K extends keyof (typeof this)['data']>(
    key: K,
  ): ReturnType<(typeof this)['data'][K]> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return this.data[key]() as ReturnType<(typeof this)['data'][K]>
  },
  isChinaRegion(): boolean {
    return this.data.region() === 'cn'
  },
  isAmerRegion(): boolean {
    return this.data.region() === 'us-east-1'
  },
  regionSpecificApi(region: string): string {
    if (this.data.isStagingDevEnv()) {
      return this.data.api()
    }

    return (
      this.get('customBaseURL') + '/' + getRegionFromName(region).api_v1_path
    )
  },
  data: {
    region(): string {
      return region
    },

    demoSpace(): string {
      return '43698'
    },

    demoEditorSpace(): string {
      return '329757'
    },

    environment(): Environment {
      return environment()
    },

    // Development environments where we override the API endpoint
    isStagingDevEnv(): boolean {
      const host = this.apiHost()
      return /app-staging|api-develop|api-staging-|new-staging|localhost:3001|api-preprod|preprod|preview-storyblok/.test(
        host,
      )
    },
    customBaseURL(): string {
      return (
        process.env.VUE_APP_STORYBLOK_CUSTOM_API_ENDPOINT ||
        'https://app.storyblok.com'
      )
    },

    secureUrl(): string {
      if (environment() === 'development') {
        return 'https://localhost:3300'
      }
      if (window.location.href.indexOf('app.storyblok.com') === -1) {
        return 'https://' + window.location.host
      }
      return 'https://app.storyblok.com'
    },

    bucket(): string {
      return regionSetting[region].bucket
    },

    s3Endpoint(): string {
      return regionSetting[region].s3Endpoint
    },

    imageService(): string {
      return regionSetting[region].imageService
    },

    imgPrefix(): string {
      return regionSetting[region].imgPrefix || ''
    },

    isDefaultApi(): boolean {
      return this.apiHost() === 'https://app.storyblok.com'
    },

    isBringYourOwnCloud(): boolean {
      if (this.isStagingDevEnv() || environment() !== 'production') {
        return false
      }

      // this also applies to the chinese environment
      return typeof window.STORYBLOK_API_ENDPOINT !== 'undefined'
    },

    apiHost(): string {
      let api = 'https://app.storyblok.com'

      if (window.location.host === 'app-beta.storyblok.com') {
        api = 'https://app-beta.storyblok.com'
      }
      if (window.location.href.indexOf('localhost:3400') > -1) {
        api = 'http://localhost:3001'
      }
      if (window.location.href.indexOf('localhost:3402') > -1) {
        api = 'https://localhost:3002'
      }
      if (
        typeof window.STORYBLOK_API_ENDPOINT !== 'undefined' &&
        window.STORYBLOK_API_ENDPOINT !== ''
      ) {
        api = window.STORYBLOK_API_ENDPOINT
      }

      return api
    },

    cdnApi(
      version = 'v1',
      region: AvailableRegionConfig | null = null,
    ): string {
      let api = 'https://api.storyblok.com'

      if (
        !isNull(region) &&
        !isUndefined(regionSetting[region]) &&
        !isUndefined(regionSetting[region].apiEndpoint)
      ) {
        api = regionSetting[region].apiEndpoint
      }

      if (window.location.href.indexOf('localhost:3400') > -1) {
        api = 'http://localhost:3001'
      }
      if (
        typeof window.STORYBLOK_API_ENDPOINT !== 'undefined' &&
        window.STORYBLOK_API_ENDPOINT !== ''
      ) {
        api = window.STORYBLOK_API_ENDPOINT
      }

      return api + '/' + version
    },

    api(): string {
      return this.apiHost() + '/v1'
    },

    pluginApiHost(): string {
      let api = 'https://plugins.storyblok.com'

      if (window.location.href.indexOf('localhost:3400') > -1) {
        api = 'http://localhost:3001'
      }
      if (
        typeof window.STORYBLOK_PLUGIN_ENDPOINT !== 'undefined' &&
        window.STORYBLOK_PLUGIN_ENDPOINT !== ''
      ) {
        api = window.STORYBLOK_PLUGIN_ENDPOINT
      }

      return api
    },

    pluginApi(): string {
      return this.pluginApiHost() + '/v1'
    },

    pluginServerlessApiUrl(): string {
      return (
        process.env.VUE_APP_PLUGIN_SERVERLESS_ENDPOINT ||
        'https://app-functions-storyblok-com.vercel.app'
      )
    },

    ignoringRegions(): boolean {
      return !!window.IGNORING_REGIONS
    },
  },
}
